import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  logout,
  PermissionUser,
  setActiveProfile,
  setAuth,
  setUnreadNotificationCount,
} from "@/store/slices/authSlice";
import Notificationicon from "@styles/all-images/icons/notification-bell-icon.svg";
import UserDropdown from "@styles/all-images/icons/user-dropdown.svg";
import actionsHandler from "@/store/actions/actionHandler";
import { AppDispatch } from "@/store";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import ChangePassword from "@/pages/ChangePassword";
import { Skeleton } from "@mui/material";
import { toast } from "react-toastify";
import { ProfileApprovalStatus } from "@/constants/enums/approval-status.enum";
import { EndpointType } from "@/constants/api";
import { NAVBAR_BG_HEXACODE } from "@/constants/constant-values";

const HeaderSkeleton = () => (
  <div className="tw-relative">
    <Skeleton
      animation="wave"
      variant="rectangular"
      width={150}
      height={40}
      className="tw-rounded-lg"
    />
  </div>
);

const Header = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const activeProfile = useSelector(
    (state: RootState) => state.auth.activeProfile,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Set "Operational User" as the initial selected value
  // const [selectedValue, setSelectedValue] = useState<PermissionUser>(userData?.permission_profiles[0]);

  const handleActiveProfileApiCall = async (user?: PermissionUser) => {
    try {
      dispatch(setActiveProfile(null));
      const defaultActiveUser =
        user ||
        userData?.permission_profiles.find(
          (e: PermissionUser) => e.id == userData?.default_profile?.id,
        ) ||
        userData?.permission_profiles[0];
      // setIsLoading(true);
      const endpoint: EndpointType = "activeProfile";
      const { authenticatedCall } = actionsHandler(endpoint);
      const response = await dispatch(
        authenticatedCall({
          method: "put",
          payload: {
            default_profile: defaultActiveUser?.id,
          },
        }),
      );

      if (response.ok) {
        dispatch(setActiveProfile(defaultActiveUser));
        navigate(0);
      }
    } catch (error) {
      console.error("error: ", error);
    } finally {
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      !userData?.default_profile?.id &&
      userData?.permission_profiles?.length
    ) {
      handleActiveProfileApiCall();
    }
  }, [userData]);

  useEffect(() => {
    if (userData?.permission_profiles && activeProfile?.name) {
      // Add a small delay before transitioning
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [userData?.permission_profiles, activeProfile?.name]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (value: PermissionUser) => {
    handleActiveProfileApiCall(value);
    setIsOpen(false); // Close the dropdown after selection
  };

  const [isRightOpen, setIsRightOpen] = useState(false);
  let userName = JSON.parse(localStorage.getItem("user") || "{}");
  const [selectedOption, setSelectedOption] = useState(() => {
    // Set initial value based on current route
    let userName = JSON.parse(localStorage.getItem("user") || "{}");

    if (location.pathname === "/my-account") return "My Account";
    if (location.pathname === "/change-password") return "Change Password";
    return `${userName?.first_name} ${userName?.last_name}`;
  });

  // Update selection when route changes
  useEffect(() => {
    if (location.pathname === "/my-account") {
      setSelectedOption("My Account");
    } else if (location.pathname === "/change-password") {
      setSelectedOption("Change Password");
    }
  }, [location.pathname]);

  const righttoggleDropdown = () => {
    setIsRightOpen(!isRightOpen);
  };

  const { authenticatedCall: logoutCall } = actionsHandler("logout");

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  const handleLogoutConfirm = async () => {
    try {
      const response = await dispatch(
        logoutCall({
          method: "delete",
        }),
      );
      dispatch(logout());

      toast.success(
        response?.data?.message || "You have successfully logged out.",
      );
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("An error occurred during logout. Please try again.");
      // Still logout user locally even if API fails
      dispatch(logout());
      navigate("/");
    }
    setOpenLogoutDialog(false);
  };

  const righthandleOptionClick = (value: string) => {
    // setSelectedOption(value);
    setIsRightOpen(false);

    if (value === "Logout") {
      setOpenLogoutDialog(true);
    }
  };

  const handleMyAccount = () => {
    setIsRightOpen(false);
    setSelectedOption("My Account");
    navigate("/my-account");
  };

  const unreadNotificationCount = useSelector(
    (state: RootState) => state.auth.unreadNotificationCount,
  );

  const fetchUserData = useCallback(async () => {
    try {
      const { authenticatedCall } = actionsHandler("onboardedUsers");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
        }),
      );
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (response.ok) {
        const assignColors = (users: PermissionUser[], colors: string[]) => {
          return users.map((user: PermissionUser, index: number) => ({
            ...user,
            color: colors[index % colors.length],
          }));
        };
        const updatedProfiles = assignColors(
          response.data.permission_profiles,
          NAVBAR_BG_HEXACODE,
        );
        dispatch(
          setUnreadNotificationCount(response.data.notification_un_read_count),
        );
        setUserData({
          ...response.data,
          permission_profiles: updatedProfiles,
        });
        dispatch(
          setAuth({
            token: user.token,
            user: { ...user, ...response.data },
          }),
        );

        const activeProfile = updatedProfiles.find(
          (profile: PermissionUser) =>
            profile.id === response.data.default_profile?.id,
        );
        if (activeProfile) {
          dispatch(setActiveProfile(activeProfile));
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return (
    <>
      <header
        className={`${activeProfile?.color || "tw-bg-[#ffffff]"} tw-h-[80px] tw-top-0 tw-fixed tw-w-full tw-z-20 tw-px-8 tw-py-4 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-transition-opacity tw-duration-300`}
      >
        <div className="tw-cursor-pointer" onClick={() => navigate("/menu")}>
          <img
            src="/logo/Logo.svg"
            alt="Logo"
            className="tw-mx-auto tw-max-w-[139px] tw-max-h-[32px]"
          />
        </div>
        {userData?.permission_profiles?.length > 0 &&
          userData?.status == ProfileApprovalStatus.O &&
          (!userData?.permission_profiles ||
          !activeProfile?.name ||
          isTransitioning ? (
            <HeaderSkeleton />
          ) : (
            userData?.status > ProfileApprovalStatus.BR &&
            userData?.status !== ProfileApprovalStatus.R && (
              <>
                <div className="tw-relative">
                  <button
                    className="tw-flex tw-items-center tw-px-4 tw-py-2 tw-bg-transparent tw-text-black tw-rounded-md"
                    onClick={toggleDropdown}
                  >
                    <span className="tw-mr-3 tw-font-semibold tw-text-[#535862]">
                      {activeProfile?.name}
                    </span>
                    <svg
                      className={`tw-w-4 tw-h-4 tw-transform ${isOpen ? "tw-rotate-180" : "tw-rotate-0"}`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="tw-absolute tw-right-0 tw-w-56 tw-bg-white tw-rounded-xl tw-shadow-lg tw-border-gray-200 tw-border tw-p-3 tw-z-[100]">
                      <ul className="tw-py-1">
                        {userData?.permission_profiles.map(
                          (user: PermissionUser) => (
                            <li
                              key={user.id}
                              onClick={() => handleOptionClick(user)}
                              className="tw-px-4 tw-py-1 tw-hover:bg-gray-100 tw-cursor-pointer"
                            >
                              {user.name}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )
          ))}
        <div className="tw-flex tw-items-center">
          <div className="tw-relative tw-mr-5 tw-cursor-pointer">
            <Link to="/notification">
              <img src={Notificationicon} alt="Notification" />
              {unreadNotificationCount !== 0 && (
                <span className="tw-absolute tw-top-[2px] tw-right-[2px] tw-w-2 tw-h-2 tw-bg-red-500 tw-rounded-full" />
              )}
            </Link>
          </div>
          <div className="tw-relative">
            <button
              className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-bg-transparent tw-border tw-border-[#CBD5E1] tw-text-black tw-rounded-lg tw-w-[250px]"
              onClick={righttoggleDropdown}
            >
              <div className="tw-flex tw-items-center">
                <img src={UserDropdown} alt="" />
                <span className="tw-mr-3 tw-ml-3 tw-font-semibold tw-text-[#535862]">
                  {selectedOption}
                </span>
              </div>
              <div>
                <svg
                  className={`tw-w-4 tw-h-4 tw-transform ${
                    isRightOpen ? "tw-rotate-180" : "tw-rotate-0"
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            {isRightOpen && (
              <div className="tw-absolute tw-right-0 tw-w-56 tw-bg-white tw-rounded-xl tw-shadow-lg tw-border-gray-200 tw-mt-2 tw-p-3">
                <ul className="tw-py-1">
                  {userData?.status > ProfileApprovalStatus.BR &&
                    userData?.status !== ProfileApprovalStatus.R && (
                      <>
                        <button
                          className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-hover:bg-gray-100 tw-cursor-pointer tw-border-0 tw-bg-transparent"
                          onClick={() => {
                            handleMyAccount();
                            setIsRightOpen(false);
                          }}
                        >
                          My Account
                        </button>
                        <button
                          className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-hover:bg-gray-100 tw-cursor-pointer tw-border-0 tw-bg-transparent"
                          onClick={() => {
                            setIsModalOpen(true);
                            setIsRightOpen(false);
                          }}
                        >
                          Change Password
                        </button>
                      </>
                    )}
                  <button
                    className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-hover:bg-gray-100 tw-cursor-pointer tw-border-0 tw-bg-transparent"
                    onClick={() => {
                      righthandleOptionClick("Logout");
                      setIsRightOpen(false);
                    }}
                  >
                    Logout
                  </button>
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>
      {openLogoutDialog && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">Confirm Logout</h2>
              <button onClick={() => setOpenLogoutDialog(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-flex-col tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold">
                Are you sure you want to logout?
              </h6>
              <p className="tw-text-[#232323] tw-text-sm tw-font-normal">
                You will be redirected to the login page.
              </p>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setOpenLogoutDialog(false)}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <ChangePassword
          isModalOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
